import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useT, useFieldError } from '@kmgestao/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  toast,
} from '@kmgestao/ui';

import {
  byokFormSchema,
  useClinicAvailableProviders,
  useSetClinicByok,
  type ByokFormInput,
  type ClinicByokRow,
} from '../../../../features/ai-clinic/index.js';

export interface ByokFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  /** `null` = criar; objeto = rotacionar. */
  current: ClinicByokRow | null;
}

export function ByokForm({ open, onOpenChange, clinicId, current }: ByokFormProps) {
  const { t } = useT('ai-clinic');
  const { t: tCommon } = useT('common');
  const tError = useFieldError();

  const providers = useClinicAvailableProviders();
  const mutation = useSetClinicByok();
  const isRotate = current !== null;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ByokFormInput>({
    resolver: zodResolver(byokFormSchema),
    values: {
      providerId: current?.providerId ?? '',
      apiKey: '',
      modelId: current?.modelId ?? '',
      nickname: current?.nickname ?? '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync({
        clinicId,
        providerId: values.providerId,
        apiKey: values.apiKey,
        modelId: values.modelId || null,
        nickname: values.nickname || null,
      });
      toast.success(isRotate ? t('byok.success.rotated') : t('byok.success.created'));
      reset();
      onOpenChange(false);
    } catch (err) {
      const fallback = isRotate ? t('byok.error.rotateFailed') : t('byok.error.createFailed');
      toast.error(err instanceof Error ? err.message : fallback);
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isRotate ? t('byok.form.titleRotate') : t('byok.form.titleCreate')}
          </DialogTitle>
          <DialogDescription>{t('byok.form.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="providerId">{t('byok.form.fields.provider.label')}</Label>
            <Select
              id="providerId"
              hasError={!!errors.providerId}
              disabled={providers.isLoading || isRotate}
              {...register('providerId')}
            >
              <option value="">{t('byok.form.fields.provider.placeholder')}</option>
              {(providers.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </Select>
            <p className="text-sm text-muted-foreground">
              {tError(errors.providerId) ?? t('byok.form.fields.provider.hint')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="apiKey">{t('byok.form.fields.apiKey.label')}</Label>
            <Input
              id="apiKey"
              type="password"
              autoComplete="new-password"
              placeholder={t('byok.form.fields.apiKey.placeholder')}
              hasError={!!errors.apiKey}
              {...register('apiKey')}
            />
            <p className="text-sm text-muted-foreground">
              {tError(errors.apiKey) ?? t('byok.form.fields.apiKey.hint')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="modelId">{t('byok.form.fields.modelId.label')}</Label>
            <Input
              id="modelId"
              autoComplete="off"
              placeholder={t('byok.form.fields.modelId.placeholder')}
              {...register('modelId')}
            />
            <p className="text-sm text-muted-foreground">{t('byok.form.fields.modelId.hint')}</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nickname">{t('byok.form.fields.nickname.label')}</Label>
            <Input
              id="nickname"
              autoComplete="off"
              placeholder={t('byok.form.fields.nickname.placeholder')}
              {...register('nickname')}
            />
            <p className="text-sm text-muted-foreground">{t('byok.form.fields.nickname.hint')}</p>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                {tCommon('actions.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending
                ? tCommon('status.saving')
                : isRotate
                  ? t('byok.form.submit.rotate')
                  : t('byok.form.submit.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
